import { Table } from "antd";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { getEmployeeList, saveEmployeeDetails } from "../../store/service";
import * as ConfigurationActions from "../../store/configurationService/actions";
import { DialogComponent } from "../common/DialogComponent";
import NoEmployee from "./NoEmployee";
import StatusUpdateDialog from "./StatusUpdateDialog";
import EditEmployee from "./EditEmployee";
import { Notification } from "../common/Notification";
import EmployeeMenuOption from "./EmployeeMenuOption";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../store";

interface IState {
    filter: string;
    filterOptions: any;
    LicenseCount: number;
    LicenseAvailable: number;
}

const filterOptions = ["Active", "Cancelled", "Pending"];
const getFilterOption: any = {
    Active: [{ label: "Cancel Employee", value: "CANCEL" }],
    Cancelled: [{ label: "Reactivate Employee", value: "REACTIVATE" }],
    Pending: [
        { label: "Resend Invite", value: "RESEND-INVITE" },
        { label: "Cancel Employee", value: "CANCEL" },
    ],
};

function Employees() {
    const history = useHistory();
    const dispatch = useDispatch();
    const routeLoading = useSelector((state: ApplicationState) => state.configurationState && (state.configurationState.loading || false));
    const [loading, setLoading] = useState<boolean>(false);
    const [statusLoading, setStatusLoading] = useState<boolean>(false);
    const [state, setState] = useState<IState>({
        filter: "All",
        filterOptions: [],
        LicenseCount: 0,
        LicenseAvailable: 0,
    });
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [employeeList, setEmployeeList] = useState([]);
    const [isConfirm, setIsConfirm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentRecord, setCurrentRecord] = useState<any>({});
    const [isBulk, setIsBulk] = useState(false);
    const [bulkOption, setBulkOption] = useState("");
    const [editRecord, setEditRecord] = useState("");
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const itemsRef = useRef<any>([]);

    const getEmployee = (status: string = "All", onLoad: boolean = false) => {
        setLoading(true);
        getEmployeeList(status)
            .then((res: any) => {
                setLoading(false);
                if (!res.HasError) {
                    setEmployeeList(res.EmployeeList);
                    if (onLoad) {
                        let filterOpt: any = ["All"];
                        if (res.ActiveCount) {
                            filterOpt.push("Active");
                        }
                        if (res.CancelledCount) {
                            filterOpt.push("Cancelled");
                        }
                        if (res.IncompleteCount) {
                            filterOpt.push("Incomplete");
                        }
                        if (res.PendingCount) {
                            filterOpt.push("Pending");
                        }
                        setState({
                            ...state,
                            filter: status,
                            filterOptions: filterOpt,
                            LicenseCount: res.LicenseCount,
                            LicenseAvailable: res.LicenseAvailable,
                        });
                    } else {
                        setState({
                            ...state,
                            filter: status,
                            LicenseCount: res.LicenseCount,
                            LicenseAvailable: res.LicenseAvailable,
                        });
                    }
                }
            })
            .catch((e) => {
                setLoading(false);
            });
    };

    const handleOptionClose = () => {
        setEditRecord("");
        setAnchorEl(null);
    };

    useEffect(() => {
        getEmployee("All", true);
    }, []);

    useEffect(() => {
        if (routeLoading) {
            dispatch(ConfigurationActions.actionCreators.setRouteLoading(false));
        }
    }, [dispatch, routeLoading]);

    const onUpdateClick = (current: any, key: string) => {
        handleOptionClose();
        if (key === "EDIT") {
            setIsEdit(true);
            setCurrentRecord({ ...current, Action: "" });
        } else {
            setCurrentRecord({ ...current, Action: key });
            setIsConfirm(true);
        }
        setIsBulk(false);
        setSelectedRowKeys([]);
    };

    const columns: any = [
        {
            title: "First Name",
            dataIndex: "FirstName",
            sorter: (a: any, b: any) => a.FirstName.localeCompare(b.FirstName),
            showSorterTooltip: false,
        },
        {
            title: "Last Name",
            dataIndex: "LastName",
            sorter: (a: any, b: any) => a.LastName.localeCompare(b.LastName),
            defaultSortOrder: "ascend",
            showSorterTooltip: false,
        },
        {
            title: "Email Address",
            dataIndex: "Email",
            className: "hide-mobile",
        },
        {
            title: "Mobile",
            dataIndex: "SMSContact",
            className: "hide-mobile",
        },
        {
            title: "Status",
            dataIndex: "Status",
            sorter: (a: any, b: any) => a.Status.localeCompare(b.Status),
            showSorterTooltip: false,
        },
        {
            title: "",
            render(current: any, row: any) {
                return {
                    props: {
                        style: { verticalAlign: "middle" },
                    },
                    children:
                        (current.Status === "Cancelled" || current.Status === "Incomplete") && state.LicenseAvailable === 0 ? null : (
                            <div style={{ position: "relative" }} ref={(el) => (itemsRef.current[current.Id] = el)}>
                                <span
                                    className="fs-1-5 pointer"
                                    onClick={(event: any) => {
                                        event.preventDefault();
                                        if (editRecord) {
                                            handleOptionClose();
                                        } else {
                                            setEditRecord(row.Id);
                                            setAnchorEl(itemsRef.current[current.Id]);
                                        }
                                    }}
                                >
                                    <em className="las la-ellipsis-v"></em>
                                </span>
                                {row.Id === editRecord && (
                                    <EmployeeMenuOption
                                        handleOptionClose={handleOptionClose}
                                        itemsRef={itemsRef}
                                        onUpdateClick={onUpdateClick}
                                        current={current}
                                        editRecord={editRecord}
                                    />
                                )}
                            </div>
                        ),
                };
            },
        },
    ];

    const onFilterChange = (currentFilter: string) => {
        getEmployee(currentFilter);
        setSelectedRowKeys([]);
    };

    const onSelectChange = (selectedRowKeys: any, selectedRows: any) => {
        if (state.filter === "Cancelled" && selectedRowKeys.length > state.LicenseAvailable) {
            Notification({
                type: "error",
                title: "Error",
                message: `Only ${state.LicenseAvailable} ${state.LicenseAvailable === 1 ? "Employee" : "Employees"} Can Be Reactivated`,
                duration: 5,
            });
        } else {
            setSelectedRowKeys(selectedRowKeys);
        }
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const getSuccessMsg = (status: string) => {
        switch (status) {
            case "CANCEL":
                return "Employee Successfully Cancelled";

            case "REACTIVATE":
                return "Employee Successfully Reactivated";

            case "RESEND-INVITE":
                return "Employee Successfully Invited";

            default:
                return "Employee Successfully Updated";
        }
    };

    const getBulkSuccessMsg = (status: string) => {
        switch (status) {
            case "CANCEL":
                return "Employee(s) Successfully Cancelled";

            case "REACTIVATE":
                return "Employee(s) Successfully Reactivated";

            case "RESEND-INVITE":
                return "Employee(s) Successfully Invited";

            default:
                return "Employee(s) Successfully Updated";
        }
    };

    const handleContinue = () => {
        setStatusLoading(true);
        let bulkRecord = [];
        if (isBulk) {
            bulkRecord = employeeList
                .filter((item: any) => selectedRowKeys.includes(item.Id))
                .reduce((cur: any[], current: any) => {
                    current["Action"] = bulkOption;
                    cur.push(current);
                    return cur;
                }, []);
        }
        saveEmployeeDetails({
            EmployeeList: isBulk ? bulkRecord : [currentRecord],
        })
            .then((res) => {
                setStatusLoading(false);
                if (res.hasError) {
                    Notification({
                        type: "error",
                        title: "Error",
                        message: res.errors[0],
                        duration: 5,
                    });
                } else {
                    Notification({
                        type: "success",
                        title: "Success",
                        message: isBulk ? getBulkSuccessMsg(bulkOption) : getSuccessMsg(currentRecord && currentRecord.Action ? currentRecord.Action : ""),
                        duration: 5,
                    });
                    closeDialog();
                    getEmployee("All", true);
                }
            })
            .catch((err) => {
                setStatusLoading(false);
                Notification({
                    type: "error",
                    title: "Error",
                    message: err,
                    duration: 5,
                });
            });
        setBulkOption("");
        setSelectedRowKeys([]);
    };

    const closeDialog = () => {
        setIsConfirm(false);
        setIsBulk(false);
        setBulkOption("");
    };

    const closeEditDialog = () => {
        setIsEdit(false);
    };

    const onSelectBulkOption = (val: string) => {
        setBulkOption(val);
        if (val) {
            setIsBulk(true);
            setIsConfirm(true);
        }
    };

    const renderTable = () => {
        return <Table columns={columns} rowKey="Id" dataSource={employeeList} pagination={false} sortDirections={["ascend", "descend", "ascend"]} />;
    };

    return (
        <Fragment>
            <div className="row flex-center">
                <div className="col-xs-6">
                    <h1 className="title-page">Employees</h1>
                </div>
                <div className="col-xs-6 flex-end mtb-1rem">
                    <button
                        type="button"
                        className="btn-default btn-primary"
                        id="btn-add-employee"
                        disabled={!state.LicenseAvailable}
                        onClick={(e) => {
                            e.preventDefault();
                            history.push({
                                pathname: "/add-employee",
                                state: {
                                    licenseAvailable: state.LicenseAvailable,
                                },
                            });
                        }}
                    >
                        <span className="btn-text">Add Employee(s)</span>
                    </button>
                </div>
            </div>
            <div className="row">
                <div className="col-xs-12  col-no-gutter flex-grow-null">
                    <div className={loading ? "loading box margin-1 h-400" : employeeList.length > 0 ? "box margin-1" : "box h-400 margin-1"}>
                        {!loading && employeeList.length > 0 ? (
                            <div className="box-header justify-space-bt pb-24">
                                <div className="col-xs-10 col-sm-10">
                                    <span className="employee-title">
                                        {state.LicenseCount - state.LicenseAvailable} of {state.LicenseCount} Employees Added
                                    </span>
                                </div>
                                <div className="col-xs-2 col-sm-2 col-no-gutter flex-grow-null">
                                    <div className="form-row inline no-margin">
                                        <div className="form-input">
                                            <select
                                                className="inputbox"
                                                id="employee-status"
                                                value={state.filter}
                                                onChange={(e) => onFilterChange(e.target.value)}
                                                name="employee-status"
                                            >
                                                {state.filterOptions.map((item: string) => (
                                                    <option key={item} value={item}>
                                                        {item}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {!loading && employeeList.length > 0 ? (
                            <div className="box-content">
                                {filterOptions.includes(state.filter) ? (
                                    state.filter === "Cancelled" && state.LicenseAvailable === 0 ? (
                                        renderTable()
                                    ) : (
                                        <Table
                                            columns={columns}
                                            dataSource={employeeList}
                                            rowClassName="employee-table-row"
                                            pagination={false}
                                            rowSelection={rowSelection}
                                            rowKey="Id"
                                            sortDirections={["ascend", "descend", "ascend"]}
                                        />
                                    )
                                ) : (
                                    renderTable()
                                )}

                                {filterOptions.includes(state.filter) && selectedRowKeys.length > 0 && (
                                    <div className="row flex-center pt-1">
                                        <div className="col-xs-2 col-sm-1 col-no-gutter flex-grow-null">
                                            <legend className="legend-label">{selectedRowKeys.length} Selected</legend>
                                        </div>
                                        <div className="col-xs-2 col-sm-2 col-no-gutter flex-grow-null">
                                            <div className="form-row inline no-margin">
                                                <div className="form-input">
                                                    <select
                                                        className="inputbox"
                                                        id="employee-bulk-status"
                                                        value={bulkOption}
                                                        onChange={(e) => onSelectBulkOption(e.target.value)}
                                                        name="employee-bulk-status"
                                                    >
                                                        <option key="" value="">
                                                            Select Action
                                                        </option>
                                                        {getFilterOption[state.filter].map((item: any) => (
                                                            <option key={item.value} value={item.value}>
                                                                {item.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : loading ? null : (
                            <NoEmployee licenseCount={state.LicenseCount} />
                        )}
                    </div>
                </div>
            </div>
            {isConfirm && (
                <DialogComponent dialogId="dialog-employee-status-confirm" title="Employee Status Update" closeDialog={closeDialog}>
                    <StatusUpdateDialog
                        isLoading={statusLoading}
                        status={isBulk ? bulkOption : currentRecord ? currentRecord.Action : ""}
                        isBulkAction={isBulk}
                        selectedRowKeys={selectedRowKeys}
                        handleContinue={handleContinue}
                        closeDialog={closeDialog}
                    />
                </DialogComponent>
            )}
            {isEdit && (
                <DialogComponent dialogId="dialog-employee-detail-form" title="Employee Detail Update" closeDialog={closeEditDialog}>
                    <EditEmployee
                        isLoading={statusLoading}
                        currentRecord={currentRecord}
                        handleContinue={() => getEmployee("All", true)}
                        closeDialog={closeEditDialog}
                    />
                </DialogComponent>
            )}
        </Fragment>
    );
}

export default Employees;
